import { Button, Card, CardContent, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useScreenPostAction, useScreenQueue } from "../../features/ops/queries.ts";
import type { ScreenPost, ScreenPostStatus } from "../../features/ops/screen-queue.ts";
import { allowedActions, postTypeLabel, statusLabel } from "../../features/ops/screen-queue.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/screen-queue")({ component: ScreenQueueScreen });

const statusTone: Record<ScreenPostStatus, "warning" | "info" | "success" | "neutral" | "danger"> =
  {
    submitted: "warning",
    paid: "info",
    approved: "success",
    displayed: "success",
    rejected: "danger",
    removed: "neutral",
    expired: "neutral",
  };

function contentPreview(post: ScreenPost): string {
  switch (post.postType) {
    case "message":
      return post.message ?? "";
    case "warp":
      return post.igHandle ? `IG: ${post.igHandle}` : "";
    case "song_request":
      return post.songTitle ?? "";
    case "tip":
      return post.tipTargetLabel ? `→ ${post.tipTargetLabel}` : "";
  }
}

interface PostCardProps {
  post: ScreenPost;
}

function PostCard({ post }: PostCardProps) {
  const action = useScreenPostAction();
  const actions = allowedActions(post.status);
  const [rejectReason, setRejectReason] = useState("");
  const [removeReason, setRemoveReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [showRemoveInput, setShowRemoveInput] = useState(false);

  const isPending = action.isPending;
  const isConflict =
    action.isError && action.error instanceof Error && action.error.message.includes("CONFLICT");

  function handleConfirmPayment() {
    action.mutate({
      postId: post.id,
      action: "confirm-payment",
      expectedUpdatedAt: post.updatedAt,
    });
  }

  function handleApprove() {
    action.mutate({
      postId: post.id,
      action: "approve",
      expectedUpdatedAt: post.updatedAt,
    });
  }

  function handleReject() {
    action.mutate({
      postId: post.id,
      action: "reject",
      reason: rejectReason || undefined,
      expectedUpdatedAt: post.updatedAt,
    });
    setShowRejectInput(false);
    setRejectReason("");
  }

  function handleRemove() {
    action.mutate({
      postId: post.id,
      action: "remove",
      reason: removeReason || undefined,
      expectedUpdatedAt: post.updatedAt,
    });
    setShowRemoveInput(false);
    setRemoveReason("");
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        {/* Header row */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-0.5">
            <p className="font-medium text-sm">{post.displayName}</p>
            {post.tableLabel ? (
              <p className="text-muted-foreground text-xs">{post.tableLabel}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <StatusChip tone="neutral">{postTypeLabel[post.postType]}</StatusChip>
            <StatusChip tone={statusTone[post.status]}>{statusLabel[post.status]}</StatusChip>
          </div>
        </div>

        {/* Content preview */}
        {contentPreview(post) ? <p className="text-sm">{contentPreview(post)}</p> : null}

        {/* Amount + time */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-sm text-foreground">
            {formatMoney(post.amountSatang)}
          </span>
          <span>
            {new Date(post.createdAt).toLocaleTimeString("th-TH", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>

        {/* Conflict / error feedback */}
        {isConflict ? (
          <p className="text-destructive text-xs">ข้อมูลเปลี่ยนแปลงแล้ว — รีเฟรชหน้านี้แล้วลองอีกครั้ง</p>
        ) : null}

        {/* Reject reason inline input */}
        {showRejectInput ? (
          <div className="space-y-2">
            <label className="block text-xs">
              เหตุผลการปฏิเสธ (ไม่บังคับ)
              <input
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                placeholder="ระบุเหตุผล..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" disabled={isPending} onClick={handleReject}>
                ยืนยันปฏิเสธ
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowRejectInput(false);
                  setRejectReason("");
                }}
              >
                ยกเลิก
              </Button>
            </div>
            {post.status === "paid" ? (
              <p className="rounded border border-warning/40 bg-warning/10 px-3 py-2 text-warning text-xs">
                ปฏิเสธแล้ว — ต้องคืนเงินลูกค้าที่เคาน์เตอร์
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Remove reason inline input */}
        {showRemoveInput ? (
          <div className="space-y-2">
            <label className="block text-xs">
              เหตุผลการเอาลงจากจอ (ไม่บังคับ)
              <input
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                placeholder="ระบุเหตุผล..."
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" disabled={isPending} onClick={handleRemove}>
                ยืนยันเอาลงจากจอ
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowRemoveInput(false);
                  setRemoveReason("");
                }}
              >
                ยกเลิก
              </Button>
            </div>
          </div>
        ) : null}

        {/* Action buttons */}
        {!showRejectInput && !showRemoveInput && actions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {actions.includes("confirm-payment") ? (
              <Button
                size="sm"
                className="min-h-11"
                disabled={isPending}
                onClick={handleConfirmPayment}
              >
                ยืนยันรับเงิน
              </Button>
            ) : null}
            {actions.includes("approve") ? (
              <Button size="sm" className="min-h-11" disabled={isPending} onClick={handleApprove}>
                อนุมัติขึ้นจอ
              </Button>
            ) : null}
            {actions.includes("reject") ? (
              <Button
                size="sm"
                variant="outline"
                className="min-h-11"
                disabled={isPending}
                onClick={() => setShowRejectInput(true)}
              >
                ปฏิเสธ
              </Button>
            ) : null}
            {actions.includes("remove") ? (
              <Button
                size="sm"
                variant="destructive"
                className="min-h-11"
                disabled={isPending}
                onClick={() => setShowRemoveInput(true)}
              >
                เอาลงจากจอ
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ScreenQueueScreen() {
  const { data: posts = [] } = useScreenQueue();

  const actionable = posts.filter((p) =>
    (["submitted", "paid", "approved", "displayed"] as ScreenPostStatus[]).includes(p.status),
  );
  const done = posts.filter((p) =>
    (["rejected", "removed", "expired"] as ScreenPostStatus[]).includes(p.status),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Screen Queue / คิวขึ้นจอ"
        subtitle="ยืนยันรับเงินและอนุมัติโพสต์ก่อนขึ้นจอหลักในสถานที่ รีเฟรชทุก 5 วินาที"
      />

      {actionable.length === 0 && done.length === 0 ? (
        <p className="text-muted-foreground text-sm">ไม่มีรายการในคิวขณะนี้</p>
      ) : null}

      {actionable.length > 0 ? (
        <section className="space-y-3">
          <h2 className="font-display font-semibold text-sm">รอดำเนินการ ({actionable.length})</h2>
          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {actionable.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        </section>
      ) : null}

      {done.length > 0 ? (
        <section className="space-y-3">
          <h2 className="font-display font-semibold text-sm text-muted-foreground">
            เสร็จสิ้น ({done.length})
          </h2>
          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {done.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
